import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import ShiftCard from './ShiftCard';
import { ScheduleService, scheduleErrorMessage } from '../../services/scheduling';
import type { CoverageRow, CoverageWeek as Coverage, Shift } from '../../dtos/scheduling/schedule.dto';
import { dayLabel, formatMinutes } from './scheduleUtils';

export interface CoverageWeekProps {
  weekStart: string;
  today: string;
  isMobile: boolean;
  refreshKey: number;
  selectedIds: Set<string>;
  onToggleSelect: (shift: Shift) => void;
  onOpen: (shift: Shift) => void;
  onCreate: (patientId: string, patientName: string, date: string) => void;
}

/** Scheduled vs approved hours for the week: a bar, red when short, amber when over. */
const HoursBar: React.FC<{ row: CoverageRow }> = ({ row }) => {
  const approved = row.approvedMinutesPerWeek;
  if (!approved) return <span className="text-xs text-gray-500">{formatMinutes(row.scheduledMinutes)} scheduled</span>;
  const pct = Math.min(100, Math.round((row.scheduledMinutes / approved) * 100));
  const over = row.scheduledMinutes > approved;
  const short = row.scheduledMinutes < approved;
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs">
        <span className={over ? 'font-medium text-amber-700' : short ? 'font-medium text-red-700' : 'text-gray-600'}>
          {formatMinutes(row.scheduledMinutes)} / {formatMinutes(approved)}
        </span>
        {short && <span className="text-red-700">{formatMinutes(approved - row.scheduledMinutes)} uncovered</span>}
        {over && <span className="text-amber-700">over PA</span>}
      </div>
      <div className="mt-1 h-1.5 w-full rounded bg-gray-200">
        <div className={`h-1.5 rounded ${over ? 'bg-amber-500' : short ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/**
 * Patient coverage week (plan §6.1): rows are patients, columns are days. PDN coordinators staff
 * a patient's approved hours, so the gaps are what matter: empty days and the hours bar show
 * them, and tapping an empty day starts a prefilled shift. On a phone it becomes one card per
 * patient with the days listed.
 */
const CoverageWeek: React.FC<CoverageWeekProps> = ({ weekStart, today, isMobile, refreshKey, selectedIds, onToggleSelect, onOpen, onCreate }) => {
  const [data, setData] = useState<Coverage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    ScheduleService.coverage(weekStart)
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e) => live && setError(scheduleErrorMessage(e, 'Could not load coverage')));
    return () => { live = false; };
  }, [weekStart, refreshKey]);

  if (error) return <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-gray-500">Loading coverage…</div>;
  if (!data.rows.length) return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">No active patients to schedule.</div>;

  const cell = (row: CoverageRow, date: string) => {
    const day = row.days.find((d) => d.date === date);
    const shifts = day?.shifts || [];
    const held = row.patientStatus === 'on_hold';
    return (
      <div className={`min-h-[72px] space-y-1 p-1 ${date === today ? 'bg-sky-50/50' : ''}`}>
        {shifts.map((s) => (
          <ShiftCard
            key={s.id}
            shift={s}
            variant={isMobile ? 'full' : 'compact'}
            selected={selectedIds.has(s.id)}
            onSelect={onToggleSelect}
            onOpen={onOpen}
          />
        ))}
        {!held && (
          <button
            type="button"
            onClick={() => onCreate(row.patientId, row.patientName, date)}
            className={`flex w-full items-center justify-center rounded border border-dashed text-xs ${shifts.length ? 'min-h-[28px] border-transparent text-gray-300 hover:border-gray-300 hover:text-gray-500' : 'min-h-[44px] border-red-200 bg-red-50/40 text-red-400 hover:text-red-600'}`}
            aria-label={`Add a shift for ${row.patientName} on ${dayLabel(date)}`}
          >
            <Plus size={14} />{!shifts.length && <span className="ml-1">Gap</span>}
          </button>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <div className="space-y-3">
        {data.rows.map((row) => (
          <article key={row.patientId} className={`rounded-lg border p-3 ${row.patientStatus === 'on_hold' ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
            <header className="mb-2">
              <h3 className="text-sm font-semibold text-gray-900">{row.patientName}{row.patientStatus === 'on_hold' && <span className="ml-2 text-xs text-amber-700">On hold</span>}</h3>
              <div className="mt-1"><HoursBar row={row} /></div>
            </header>
            <div className="divide-y divide-gray-100">
              {data.days.map((date) => (
                <div key={date} className="flex gap-2 py-1">
                  <div className="w-12 shrink-0 pt-2 text-xs font-medium text-gray-500">{dayLabel(date)}</div>
                  <div className="min-w-0 flex-1">{cell(row, date)}</div>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full min-w-[960px] table-fixed border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-gray-50">
          <tr>
            <th scope="col" className="w-56 border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600">Patient · hours this week</th>
            {data.days.map((d) => (
              <th key={d} scope="col" className={`border-b border-l border-gray-200 px-2 py-2 text-xs font-semibold ${d === today ? 'text-sky-700' : 'text-gray-600'}`}>{dayLabel(d)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.patientId} className={row.patientStatus === 'on_hold' ? 'bg-amber-50' : ''}>
              <th scope="row" className="border-b border-gray-200 px-3 py-2 align-top font-normal">
                <div className="truncate text-sm font-semibold text-gray-900">{row.patientName}</div>
                {row.patientStatus === 'on_hold' && <div className="text-xs font-medium text-amber-700">On hold — no new shifts</div>}
                {row.patientStatus === 'pending_admission' && <div className="text-xs text-gray-500">Pending admission</div>}
                <div className="mt-1"><HoursBar row={row} /></div>
              </th>
              {data.days.map((d) => <td key={d} className="border-b border-l border-gray-200 align-top">{cell(row, d)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CoverageWeek;
